import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  DbOntologyConstraint,
  DbOntologyTable,
  useCreateDbOntologyConstraint,
  useUpdateDbOntologyNode,
} from "@/service-hooks/use-get-db-ontology-service";

interface DbOntologyConstraintDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  dataLakeLabel?: string;
  tables: DbOntologyTable[];
  constraint?: DbOntologyConstraint | null;
  defaultTableId?: string;
  onSaved?: () => void;
  isReadOnly?: boolean;
}

type ConstraintType = "PRIMARY_KEY" | "FOREIGN_KEY" | "UNIQUE" | "CHECK";

interface FormState {
  tableId: string;
  name: string;
  constraintType: ConstraintType;
  columns: string;
  onDelete: string;
  checkExpression: string;
  enabled: boolean;
  validated: boolean;
}

const EMPTY: FormState = {
  tableId: "",
  name: "",
  constraintType: "PRIMARY_KEY",
  columns: "",
  onDelete: "",
  checkExpression: "",
  enabled: true,
  validated: true,
};

const parseColumns = (raw: string): string[] => {
  const text = raw.trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  } catch {
    // fall through
  }
  return text
    .split(/[\n,]/g)
    .map((p) => p.trim())
    .filter(Boolean);
};

export default function DbOntologyConstraintDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  dataLakeLabel,
  tables,
  constraint,
  defaultTableId,
  onSaved,
  isReadOnly: readOnlyProp = false,
}: DbOntologyConstraintDialogProps) {
  const isEdit = !!constraint?.id;
  // Editing an existing index/constraint isn't supported (saving it failed with 400), so it
  // opens view-only; delete and re-create it to change it.
  const isReadOnly = readOnlyProp || isEdit;
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const createConstraint = useCreateDbOntologyConstraint();
  const updateNode = useUpdateDbOntologyNode();

  useEffect(() => {
    if (!isOpen) return;
    if (constraint) {
      const ct = (constraint.constraintType as ConstraintType) || "PRIMARY_KEY";
      setForm({
        tableId: constraint.tableId || "",
        name: constraint.name ?? "",
        constraintType: ["PRIMARY_KEY", "FOREIGN_KEY", "UNIQUE", "CHECK"].includes(ct)
          ? ct
          : "PRIMARY_KEY",
        columns: Array.isArray(constraint.columns) ? constraint.columns.join(", ") : "",
        onDelete: "",
        checkExpression: "",
        enabled: true,
        validated: true,
      });
    } else {
      setForm({ ...EMPTY, tableId: defaultTableId || "" });
    }
  }, [isOpen, constraint, defaultTableId]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.tableId) return;
    if (form.constraintType === "CHECK" && !form.checkExpression.trim()) return;
    if (form.constraintType !== "CHECK" && !parseColumns(form.columns).length) return;

    const columns = parseColumns(form.columns);
    try {
      if (isEdit && constraint?.id) {
        await updateNode.mutateAsync({
          label: "DDLConstraint",
          projectUuid,
          id: constraint.id,
          dataLakeId,
          data: {
            ...(form.name.trim() ? { name: form.name.trim() } : { name: null }),
            constraintType: form.constraintType,
            columns,
            ...(form.onDelete.trim() ? { onDelete: form.onDelete.trim() } : {}),
            ...(form.checkExpression.trim()
              ? { checkExpression: form.checkExpression.trim() }
              : {}),
            enabled: form.enabled,
            validated: form.validated,
          },
        });
      } else {
        await createConstraint.mutateAsync({
          projectUuid,
          dataLakeId,
          tableId: form.tableId,
          name: form.name.trim() || null,
          constraintType: form.constraintType,
          columns,
          onDelete: form.onDelete.trim() || undefined,
          checkExpression: form.checkExpression.trim() || undefined,
          enabled: form.enabled,
          validated: form.validated,
        });
      }
      onSaved?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  const isPending = createConstraint.isPending || updateNode.isPending;
  const isCheck = form.constraintType === "CHECK";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isReadOnly
              ? "View Constraint"
              : isEdit
                ? "Edit Constraint"
                : "Create Constraint"}
            {dataLakeLabel ? ` — ${dataLakeLabel}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure the constraint fields for the selected table.
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 overflow-y-auto flex-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Table <span className="text-error">*</span>
            </label>
            <select
              value={form.tableId}
              onChange={(e) => update("tableId", e.target.value)}
              disabled={isReadOnly || isEdit || !!defaultTableId}
              className="w-full h-10 rounded-md border border-input bg-surface text-white px-3 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="">Select a table…</option>
              {tables
                .filter((t) => !!t.id)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Name</label>
              <Input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Optional"
                readOnly={isReadOnly}
                className="bg-surface text-white"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Constraint Type</label>
              <select
                value={form.constraintType}
                onChange={(e) =>
                  update("constraintType", e.target.value as ConstraintType)
                }
                disabled={isReadOnly}
                className="w-full h-10 rounded-md border border-input bg-surface text-white px-3 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="PRIMARY_KEY">PRIMARY_KEY</option>
                <option value="FOREIGN_KEY">FOREIGN_KEY</option>
                <option value="UNIQUE">UNIQUE</option>
                <option value="CHECK">CHECK</option>
              </select>
            </div>
          </div>

          {!isCheck && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">
                Columns <span className="text-error">*</span>
              </label>
              <Input
                value={form.columns}
                onChange={(e) => update("columns", e.target.value)}
                placeholder='Comma separated, or JSON array (e.g. ["id"])'
                readOnly={isReadOnly}
                className="bg-surface text-white font-mono text-xs"
              />
            </div>
          )}

          {form.constraintType === "FOREIGN_KEY" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">On Delete</label>
              <Input
                value={form.onDelete}
                onChange={(e) => update("onDelete", e.target.value)}
                placeholder="e.g. CASCADE, SET NULL, RESTRICT"
                readOnly={isReadOnly}
                className="bg-surface text-white"
              />
            </div>
          )}

          {isCheck && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">
                Check Expression <span className="text-error">*</span>
              </label>
              <Input
                value={form.checkExpression}
                onChange={(e) => update("checkExpression", e.target.value)}
                placeholder="e.g. status IN ('A','I')"
                readOnly={isReadOnly}
                className="bg-surface text-white font-mono text-xs"
              />
            </div>
          )}

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={form.enabled}
                onCheckedChange={(checked) => update("enabled", checked)}
                disabled={isReadOnly}
              />
              <span className="text-sm text-white">Enabled</span>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.validated}
                onCheckedChange={(checked) => update("validated", checked)}
                disabled={isReadOnly}
              />
              <span className="text-sm text-white">Validated</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {isReadOnly ? "Close" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSubmit}
              disabled={
                !form.tableId ||
                (isCheck
                  ? !form.checkExpression.trim()
                  : !form.columns.trim()) ||
                isPending
              }
              className="bg-primary hover:bg-primary-dark text-white"
            >
              {isPending ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
